import { createSlice, createSelector } from '@reduxjs/toolkit'
import qs from 'qs'

const initialState = {
  cachedQueries: {},
  cachedCollectionsQueries: {},
  cachedLooksQueries: {},
  cachedShopsQueries: {},

  loading: false,
  loadingCollections: false,
  loadingLooks: false,
  loadingShops: false,
  error: null,
  errorCollections: null,
  errorLooks: null,
  errorShops: null,

  currentQuery: null,
  currentQueryCatalogGroup: null,

  loadingInfo: {},
  loadingCollectionsInfo: {},
  loadingLooksInfo: {},
  loadingShopsInfo: {},
  errorsInfo: {},
  errorsCollectionsInfo: {},
  errorsLooksInfo: {},
  errorsShopsInfo: {},
}

const product = createSlice({
  name: 'product',
  initialState: initialState,
  reducers: {
    requestProduct(state, { payload }) {
      const { key } = payload || {}

      state.loading = true
      if (key) {
        state.loadingInfo[key] = true
        state.errorsInfo[key] = null
      }
    },
    requestProductCollections(state, { payload }) {
      const { key } = payload || {}

      state.loadingCollections = true
      if (key) {
        state.loadingCollectionsInfo[key] = true
        state.errorsCollectionsInfo[key] = null
      }
    },
    requestProductLooks(state, { payload }) {
      const { key } = payload || {}

      state.loadingLooks = true
      if (key) {
        state.loadingLooksInfo[key] = true
        state.errorsLooksInfo[key] = null
      }
    },
    requestProductShops(state, { payload }) {
      const { key } = payload || {}

      state.loadingShops = true
      if (key) {
        state.loadingShopsInfo[key] = true
        state.errorsShopsInfo[key] = null
      }
    },
    successProduct(state, { payload }) {
      const { key, data, refresh = false } = payload
      const { cachedQueries } = state
      state.currentQuery = key
      if (data && (refresh || !cachedQueries[key])) {
        cachedQueries[key] = data
      }
      state.currentQueryCatalogGroup = null
      state.loading = false
      state.error = null

      state.loadingInfo[key] = false
      state.errorsInfo[key] = null
    },
    successProductCollections(state, { payload }) {
      const { key, data } = payload

      state.cachedCollectionsQueries[key] = data || state.cachedCollectionsQueries[key] || {}
      state.currentQueryCatalogGroup = key
      state.loadingCollections = false
      state.errorCollections = null

      state.loadingCollectionsInfo[key] = false
      state.errorsCollectionsInfo[key] = null
    },
    successProductLooks(state, { payload }) {
      const { key, data } = payload

      state.cachedLooksQueries[key] = data || state.cachedLooksQueries[key] || null
      state.currentQueryCatalogGroup = key
      state.loadingLooks = false
      state.errorLooks = null

      state.loadingLooksInfo[key] = false
      state.errorsLooksInfo[key] = null
    },
    successProductShops(state, { payload }) {
      const { key, data } = payload

      state.cachedShopsQueries[key] = data || state.cachedShopsQueries[key]
      state.loadingShops = false
      state.errorShops = null

      state.loadingShopsInfo[key] = false
      state.errorsShopsInfo[key] = null
    },
    failureProduct(state, { payload }) {
      const { key, data } = payload
      state.currentQuery = key
      state.loading = false
      state.error = data

      state.loadingInfo[key] = false
      state.errorsInfo[key] = data
    },
    failureProductCollections(state, { payload }) {
      const { data, key } = payload

      state.loadingCollections = false
      state.currentQueryCatalogGroup = key
      state.errorCollections = data

      state.loadingCollectionsInfo[key] = false
      state.errorsCollectionsInfo[key] = data
    },
    failureProductLooks(state, { payload }) {
      const { data, key } = payload

      state.loadingLooks = false
      state.currentQueryCatalogGroup = key
      state.errorLooks = data

      state.loadingLooksInfo[key] = false
      state.errorsLooksInfo[key] = data
    },
    failureProductShops(state, { payload }) {
      const { data, key } = payload

      state.loadingShops = false
      state.errorShops = data

      state.loadingShopsInfo[key] = false
      state.errorsShopsInfo[key] = data
    },
  },
})

export const fetchProduct = ({ refresh = false, ...params }) => async (dispatch, getState, { api }) => {
  const state = getState().product
  const key = qs.stringify(params)
  try {
    if (!refresh && state.cachedQueries[key]) {
      dispatch(successProduct({ key }))
      return
    } else {
      dispatch(requestProduct({ key }))
      const response = await api.productBack(params)
      const { result, data } = response
      if (result === 'success') {
        dispatch(successProduct({ key, data, refresh }))
      } else {
        dispatch(failureProduct({ key, data: response }))
      }
    }
  } catch (err) {
    dispatch(failureProduct({ key, data: err.toString() }))
  }
}

export const fetchProductPage = ({ refresh = false, ...params }) => async (dispatch, getState, { api }) => {
  const state = getState().product
  const key = qs.stringify(params)
  try {
    if (!refresh && state.cachedQueries[key]) {
      dispatch(successProduct({ key }))
      return
    } else {
      dispatch(requestProduct({ key }))
      const response = await api.newProductBack(params)
      const { result, data } = response
      if (result === 'success') {
        dispatch(successProduct({ key, data }))
      } else {
        dispatch(failureProduct({ key, data: response }))
      }
    }
  } catch (err) {
    dispatch(failureProduct({ key, data: err.toString() }))
  }
}

export const fetchProductCollections = ({ refresh = false, onSuccess, onError, ...params }) => async (dispatch, getState, { api }) => {
  const state = getState().product
  const key = qs.stringify(params)
  try {
    if (!refresh && state.cachedCollectionsQueries[key]) {
      dispatch(successProductCollections({ key }))
      onSuccess?.(state.cachedCollectionsQueries[key])
      return
    } else {
      dispatch(requestProductCollections({ key }))
      const response = await api.productFromCollections(params)
      const { result, data } = response

      if (result === 'success') {
        dispatch(successProductCollections({ key, data }))
        onSuccess?.(data)
      } else {
        dispatch(failureProductCollections({ key, data: response }))
        onError?.(response)
      }
    }
  } catch (err) {
    dispatch(failureProductCollections({ key, data: err.toString() }))
    onError?.(err.toString())
  }
}

export const fetchProductLooks = ({ refresh = false, onSuccess, onError, ...params }) => async (dispatch, getState, { api }) => {
  const state = getState().product
  const key = qs.stringify(params)
  try {
    if (!refresh && state.cachedLooksQueries[key]) {
      dispatch(successProductLooks({ key }))
      onSuccess?.(state.cachedLooksQueries[key])
      return
    } else {
      dispatch(requestProductLooks({ key }))
      const response = await api.productLooks(params)
      const { result, data } = response

      if (result === 'success') {
        dispatch(successProductLooks({ key, data }))
        onSuccess?.(data)
      } else {
        dispatch(failureProductLooks({ key, data: response }))
        onError?.(response)
      }
    }
  } catch (err) {
    dispatch(failureProductLooks({ key, data: err.toString() }))
    onError?.(err.toString())
  }
}

export const fetchProductsFromCollections = ({ onSuccess, onError, ...params }) => async (_dispatch, _getState, { api }) => {
  try {
      const response = await api.productFromCollections(params)
      const { result, data } = response

      if (result === 'success') {
        onSuccess?.(data.products)
      } else {
        onError?.(response)
      }

  } catch (err) {
    onError?.(err.toString())
  }
}

export const fetchProductShops = ({ refresh = false, errToString = true, ...params }) => async (dispatch, getState, { api }) => {
  const state = getState().product
  const key = qs.stringify(params)
  try {
    if (!refresh && state.cachedShopsQueries[key]) {
      dispatch(successProductShops({ key }))
      return
    } else {
      dispatch(requestProductShops({ key }))
      const response = await api.productShops(params)
      const { result, data } = response

      if (result === 'success') {
        dispatch(successProductShops({ key, data }))
      } else {
        dispatch(failureProductShops({ key, data: response }))
      }
    }
  } catch (err) {
    dispatch(failureProductShops({ key, data: errToString ? err.toString() : err }))
  }
}

export const {
  requestProduct,
  successProduct,
  failureProduct,
  requestProductCollections,
  successProductCollections,
  failureProductCollections,
  requestProductShops,
  successProductShops,
  failureProductShops,
  requestProductLooks,
  successProductLooks,
  failureProductLooks
} = product.actions

export default product.reducer
